import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, ArrowUp, ArrowDown, Globe, Home, Tags, MapPin,
  FileText, Phone, Layout, RefreshCw, Info, CreditCard, Lock, ShieldCheck,
} from "@/lib/icons";
import LogoUpload from "@/components/LogoUpload";
import HeroImageUpload from "@/components/HeroImageUpload";
import BannerManager from "@/components/BannerManager";
import CategoryManager from "@/components/CategoryManager";
import IdCard from "@/components/IdCard";
import { useSettings } from "@/lib/settings";

const SAMPLE_MEMBER = {
  member_id: "PN009012",
  full_name: "Ramesh Kumar",
  studio_name: "Sample Studio",
  state: "Karnataka",
  district: "Bengaluru Urban",
  city: "Bengaluru",
  pincode: "560001",
  category: "Photographer",
  mobile: "9876543210",
  email: "ramesh@example.com",
  blood_group: "O+",
  created_at: new Date().toISOString(),
  validity_until: null,
  photo_url: "",
  avatar_key: "",
};

const inputCls = "h-11 bg-white/5 border-white/10 text-white placeholder:text-white/35";
const textareaCls = "min-h-[80px] bg-white/5 border-white/10 text-white placeholder:text-white/35 text-sm";

const ICON_OPTIONS = ["ShieldCheck", "MapPin", "LayoutGrid", "Camera", "Users", "Star", "Award", "Sparkles"];

export default function AdminCms() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTab, setSavingTab] = useState(null);
  const [tab, setTab] = useState("website");
  const { refresh: refreshSettings } = useSettings();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/public/settings")
      .then((r) => setData(normalize(r.data)))
      .catch(() => setData(normalize({})))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveKeys = async (keys, tabName, overrides = {}) => {
    setSavingTab(tabName);
    try {
      const payload = {};
      keys.forEach((k) => {
        payload[k] = Object.prototype.hasOwnProperty.call(overrides, k) ? overrides[k] : data[k];
      });
      const { data: fresh } = await api.patch("/admin/settings", payload);
      setData(normalize(fresh));
      toast.success("Saved");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSavingTab(null); }
  };

  const upd = (path, val) => setData((s) => setDeep({ ...s }, path, val));

  if (loading || !data) return <div className="py-16 text-white/50">Loading CMS…</div>;

  const TabBtn = ({ v, label, icon: Icon }) => (
    <TabsTrigger value={v} data-testid={`cms-tab-${v}`}
      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500/25 data-[state=active]:to-magenta-500/15 data-[state=active]:text-white text-white/60 rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5">
      <Icon size={13} /> {label}
    </TabsTrigger>
  );

  const SaveBar = ({ keys, name }) => (
    <div className="flex justify-end pt-4 border-t border-white/5">
      <button data-testid={`cms-save-${name}`} onClick={() => saveKeys(keys, name)} disabled={savingTab === name}
        className="btn-glow inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
        <Save size={14} /> {savingTab === name ? "Saving…" : "Save changes"}
      </button>
    </div>
  );

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="admin-page-heading fade-up">
        <div>
          <div className="admin-page-kicker">Website publishing</div>
          <h1 className="mt-2 font-display text-5xl text-white">Content Manager</h1>
          <p className="admin-page-description">Edit public pages, brand assets, categories and locations without touching code.</p>
        </div>
        <button onClick={load} className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-white">
          <RefreshCw size={13} /> Reload
        </button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="admin-surface sticky top-[72px] z-20 flex h-auto w-full max-w-full justify-start gap-1 overflow-x-auto p-1.5 sm:flex-wrap">
          <TabBtn v="website" label="Website" icon={Globe} />
          <TabBtn v="homepage" label="Homepage" icon={Home} />
          <TabBtn v="card" label="Card" icon={CreditCard} />
          <TabBtn v="categories" label="Categories" icon={Tags} />
          <TabBtn v="locations" label="Locations" icon={MapPin} />
          <TabBtn v="about" label="About" icon={FileText} />
          <TabBtn v="contact" label="Contact" icon={Phone} />
          <TabBtn v="privacy" label="Privacy" icon={Lock} />
          <TabBtn v="verification" label="Verification" icon={ShieldCheck} />
          <TabBtn v="footer" label="Footer" icon={Layout} />
        </TabsList>

        {/* WEBSITE */}
        <TabsContent value="website">
          <Panel title="Branding & contact">
            <Grid>
              <FieldRow label="Website name">
                <Input data-testid="cms-website-name" className={inputCls} value={data.website_name || ""} onChange={(e) => upd(["website_name"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Site logo" hint="Shown in the header and footer. Uploads instantly." span="full">
                <LogoUpload
                  src={data.logo_url}
                  onChange={(url) => { upd(["logo_url"], url || ""); refreshSettings?.(); }}
                />
              </FieldRow>
              <FieldRow label="Support email">
                <Input data-testid="cms-contact-email" className={inputCls} value={data.contact_email || ""} onChange={(e) => upd(["contact_email"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Support phone">
                <Input data-testid="cms-contact-phone" className={inputCls} value={data.contact_phone || ""} onChange={(e) => upd(["contact_phone"], e.target.value)} placeholder="+91-9999999999" />
              </FieldRow>
              <FieldRow label="Admin notification email">
                <Input data-testid="cms-admin-notif-email" className={inputCls} value={data.admin_notification_email || ""} onChange={(e) => upd(["admin_notification_email"], e.target.value)} />
              </FieldRow>
              <FieldRow label="WhatsApp number" hint="10-digit Indian number, no country code.">
                <Input data-testid="cms-whatsapp" className={inputCls} value={data.whatsapp_number || ""} onChange={(e) => upd(["whatsapp_number"], e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="9876543210" />
              </FieldRow>
              <FieldRow label="Telegram link">
                <Input data-testid="cms-telegram" className={inputCls} value={data.telegram_link || ""} onChange={(e) => upd(["telegram_link"], e.target.value)} placeholder="https://t.me/YourChannel" />
              </FieldRow>
            </Grid>
          </Panel>

          <Panel title="Social media links" subtitle="Blank URLs hide the icon from the footer.">
            <div className="grid sm:grid-cols-2 gap-3">
              {(data.social_links || []).map((s, i) => (
                <div key={s.name} className="glass rounded-xl p-3">
                  <div className="text-xs font-semibold text-white">{s.name}</div>
                  <Input data-testid={`cms-social-${s.name.toLowerCase()}`}
                    className="mt-2 h-9 bg-white/5 border-white/10 text-white text-sm"
                    value={s.url || ""}
                    onChange={(e) => upd(["social_links", i, "url"], e.target.value)}
                    placeholder="https://…" />
                </div>
              ))}
            </div>
          </Panel>

          <SaveBar keys={["website_name","logo_url","contact_email","contact_phone","admin_notification_email","whatsapp_number","telegram_link","social_links"]} name="website" />
        </TabsContent>

        {/* HOMEPAGE */}
        <TabsContent value="homepage">
          <Panel title="Hero section">
            <Grid>
              <FieldRow label="Overline / badge">
                <Input data-testid="cms-hero-badge" className={inputCls} value={data.hero?.badge || ""} onChange={(e) => upd(["hero","badge"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Title: line 1">
                <Input data-testid="cms-hero-line1" className={inputCls} value={data.hero?.title_line1 || ""} onChange={(e) => upd(["hero","title_line1"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Title: accent word" hint="Shown in italic gradient.">
                <Input data-testid="cms-hero-accent" className={inputCls} value={data.hero?.title_accent || ""} onChange={(e) => upd(["hero","title_accent"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Title: line 2">
                <Input data-testid="cms-hero-line2" className={inputCls} value={data.hero?.title_line2 || ""} onChange={(e) => upd(["hero","title_line2"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Title: tail line">
                <Input data-testid="cms-hero-tail" className={inputCls} value={data.hero?.title_tail || ""} onChange={(e) => upd(["hero","title_tail"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Hero image" hint="The portrait image next to the headline. Uploads instantly." span="full">
                <HeroImageUpload
                  src={data.hero?.image_url}
                  onChange={(url) => { upd(["hero","image_url"], url || ""); refreshSettings?.(); }}
                />
              </FieldRow>
              <FieldRow label="Description" span="full">
                <Textarea data-testid="cms-hero-desc" className={textareaCls} rows={3} value={data.hero?.description || ""} onChange={(e) => upd(["hero","description"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Image card overline">
                <Input data-testid="cms-hero-card-overline" className={inputCls} value={data.hero?.card_overline || ""} onChange={(e) => upd(["hero","card_overline"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Image card body">
                <Input data-testid="cms-hero-card-body" className={inputCls} value={data.hero?.card_body || ""} onChange={(e) => upd(["hero","card_body"], e.target.value)} />
              </FieldRow>
            </Grid>
          </Panel>

          <Panel title="Hero buttons">
            <Grid>
              {["primary","secondary","tertiary"].map((k) => (
                <React.Fragment key={k}>
                  <FieldRow label={`${cap(k)} button: label`}>
                    <Input data-testid={`cms-cta-${k}-label`} className={inputCls}
                      value={data.hero?.[`cta_${k}_label`] || ""}
                      onChange={(e) => upd(["hero", `cta_${k}_label`], e.target.value)} placeholder="Leave blank to hide" />
                  </FieldRow>
                  <FieldRow label={`${cap(k)} button: link`}>
                    <Input data-testid={`cms-cta-${k}-link`} className={inputCls}
                      value={data.hero?.[`cta_${k}_link`] || ""}
                      onChange={(e) => upd(["hero", `cta_${k}_link`], e.target.value)} placeholder="/gomore or https://…" />
                  </FieldRow>
                </React.Fragment>
              ))}
            </Grid>
          </Panel>

          <Panel title="Feature cards (3)" subtitle="Displayed under the stats on the homepage.">
            {(data.home_features || []).map((f, i) => (
              <div key={i} className="glass rounded-2xl p-4 mb-3 space-y-3">
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="overline text-white/60 text-[10px]">Icon</label>
                    <select data-testid={`cms-feature-${i}-icon`}
                      className="mt-2 w-full h-11 bg-white/5 border border-white/10 rounded-md text-white text-sm px-3"
                      value={f.icon || "ShieldCheck"} onChange={(e) => upd(["home_features", i, "icon"], e.target.value)}>
                      {ICON_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline text-white/60 text-[10px]">Title</label>
                    <Input className={`mt-2 ${inputCls}`} data-testid={`cms-feature-${i}-title`}
                      value={f.title || ""} onChange={(e) => upd(["home_features", i, "title"], e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="overline text-white/60 text-[10px]">Body</label>
                  <Textarea className={`mt-2 ${textareaCls}`} data-testid={`cms-feature-${i}-body`}
                    rows={2} value={f.body || ""} onChange={(e) => upd(["home_features", i, "body"], e.target.value)} />
                </div>
              </div>
            ))}
          </Panel>

          <Panel title="Home banner slider" subtitle="Up to 5 sliding images shown at the top of the homepage. Uploads save instantly; toggle visibility or reorder to change order.">
            <BannerManager
              value={data.home_banners || []}
              onChange={(next) => {
                upd(["home_banners"], next);
                saveKeys(["home_banners"], "homepage-banners", { home_banners: next });
              }}
            />
          </Panel>

          <SaveBar keys={["hero","home_features","home_banner_images","home_banners"]} name="homepage" />
        </TabsContent>

        {/* DIGITAL ID CARD */}
        <TabsContent value="card">
          <Panel title="Live preview" subtitle="Sample member details on the current card design, exactly as members will see it on their account page.">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="overline text-white/50 mb-3">Front</div>
                <IdCard member={SAMPLE_MEMBER} showActions={false} initialSide="front" />
              </div>
              <div>
                <div className="overline text-white/50 mb-3">Back</div>
                <IdCard member={SAMPLE_MEMBER} showActions={false} initialSide="back" />
              </div>
            </div>
          </Panel>
        </TabsContent>

        {/* CATEGORIES */}
        <TabsContent value="categories">
          <Panel title="Photography categories" subtitle="Shown on the home category strip, register form and directory filter. Uploads and toggles save on click of the Save button below.">
            <CategoryManager
              value={data.categories || []}
              onChange={(next) => upd(["categories"], next)}
            />
          </Panel>
          <SaveBar keys={["categories"]} name="categories" />
        </TabsContent>

        {/* LOCATIONS */}
        <TabsContent value="locations">
          <Panel title="Locations" subtitle="Optional lists shown as autocomplete on the register form and search filter.">
            <div className="grid md:grid-cols-3 gap-4">
              {["states", "districts", "cities"].map((k) => (
                <LocationList key={k} kind={k}
                  items={data.locations?.[k] || []}
                  onChange={(items) => upd(["locations", k], items)} />
              ))}
            </div>
          </Panel>
          <SaveBar keys={["locations"]} name="locations" />
        </TabsContent>

        {/* ABOUT */}
        <TabsContent value="about">
          <Panel title="About page">
            <Grid>
              <FieldRow label="Overline"><Input data-testid="cms-about-overline" className={inputCls} value={data.about_page?.overline || ""} onChange={(e) => upd(["about_page", "overline"], e.target.value)} /></FieldRow>
              <FieldRow label="Title prefix"><Input data-testid="cms-about-title-prefix" className={inputCls} value={data.about_page?.title_prefix || ""} onChange={(e) => upd(["about_page", "title_prefix"], e.target.value)} /></FieldRow>
              <FieldRow label="Title accent"><Input data-testid="cms-about-title-accent" className={inputCls} value={data.about_page?.title_accent || ""} onChange={(e) => upd(["about_page", "title_accent"], e.target.value)} /></FieldRow>
              <FieldRow label="Title suffix"><Input data-testid="cms-about-title-suffix" className={inputCls} value={data.about_page?.title_suffix || ""} onChange={(e) => upd(["about_page", "title_suffix"], e.target.value)} /></FieldRow>
              <FieldRow label="Description" span="full">
                <Textarea data-testid="cms-about-desc" className={textareaCls} rows={4} value={data.about_page?.description || ""} onChange={(e) => upd(["about_page", "description"], e.target.value)} />
              </FieldRow>
            </Grid>
            <div className="mt-6">
              <div className="overline text-white/50 mb-3">Pillar cards</div>
              {(data.about_page?.pillars || []).map((p, i) => (
                <div key={i} className="glass rounded-xl p-4 mb-3 space-y-3" data-testid={`cms-about-pillar-${i}`}>
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-white/50">Pillar #{i + 1}</div>
                    <IconBtn testid={`cms-about-pillar-del-${i}`} tone="red" onClick={() => upd(["about_page", "pillars"], data.about_page.pillars.filter((_, idx) => idx !== i))} Icon={Trash2} />
                  </div>
                  <Input className={inputCls} placeholder="Title" data-testid={`cms-about-pillar-title-${i}`}
                    value={p.title || ""} onChange={(e) => upd(["about_page", "pillars", i, "title"], e.target.value)} />
                  <Textarea className={textareaCls} rows={2} placeholder="Body" data-testid={`cms-about-pillar-body-${i}`}
                    value={p.body || ""} onChange={(e) => upd(["about_page", "pillars", i, "body"], e.target.value)} />
                </div>
              ))}
              <button data-testid="cms-about-pillar-add"
                onClick={() => upd(["about_page", "pillars"], [...(data.about_page?.pillars || []), { title: "New pillar", body: "" }])}
                className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white">
                <Plus size={14} /> Add pillar
              </button>
            </div>
          </Panel>
          <Panel title="Our story / mission" subtitle="Shown inside the expandable 'About PRONET' card. Separate paragraphs with a blank line.">
            <ParagraphsField testid="cms-about-body" value={data.about_page?.body_paragraphs}
              onCommit={(paras) => upd(["about_page", "body_paragraphs"], paras)} />
          </Panel>
          <SaveBar keys={["about_page"]} name="about" />
        </TabsContent>

        {/* CONTACT */}
        <TabsContent value="contact">
          <Panel title="Contact page">
            <Grid>
              <FieldRow label="Overline"><Input data-testid="cms-contact-overline" className={inputCls} value={data.contact_page?.overline || ""} onChange={(e) => upd(["contact_page", "overline"], e.target.value)} /></FieldRow>
              <FieldRow label="Title prefix"><Input data-testid="cms-contact-title-prefix" className={inputCls} value={data.contact_page?.title_prefix || ""} onChange={(e) => upd(["contact_page", "title_prefix"], e.target.value)} /></FieldRow>
              <FieldRow label="Title accent"><Input data-testid="cms-contact-title-accent" className={inputCls} value={data.contact_page?.title_accent || ""} onChange={(e) => upd(["contact_page", "title_accent"], e.target.value)} /></FieldRow>
              <FieldRow label="Studio line"><Input data-testid="cms-contact-studio" className={inputCls} value={data.contact_page?.studio_line || ""} onChange={(e) => upd(["contact_page", "studio_line"], e.target.value)} /></FieldRow>
              <FieldRow label="Address" span="full"><Textarea data-testid="cms-contact-address" className={textareaCls} rows={2} value={data.contact_page?.address || ""} onChange={(e) => upd(["contact_page", "address"], e.target.value)} /></FieldRow>
              <FieldRow label="Description" span="full"><Textarea data-testid="cms-contact-desc" className={textareaCls} rows={3} value={data.contact_page?.description || ""} onChange={(e) => upd(["contact_page", "description"], e.target.value)} /></FieldRow>
              <FieldRow label="Show 'Admin login' button" hint="Discreet button at the bottom of the contact page.">
                <div className="flex items-center gap-3 h-11">
                  <Switch data-testid="cms-contact-show-admin"
                    checked={!!data.contact_page?.show_admin_link}
                    onCheckedChange={(v) => upd(["contact_page", "show_admin_link"], v)} />
                  <span className="text-sm text-white/70">{data.contact_page?.show_admin_link ? "Visible" : "Hidden"}</span>
                </div>
              </FieldRow>
            </Grid>
          </Panel>
          <SaveBar keys={["contact_page"]} name="contact" />
        </TabsContent>

        {/* PRIVACY */}
        <TabsContent value="privacy">
          <Panel title="Privacy policy" subtitle="Full text shown on the /privacy page. Separate paragraphs with a blank line.">
            <FieldRow label="Last updated" hint='Shown as "Last updated: …" under the page title.'>
              <Input data-testid="cms-privacy-updated" className={inputCls} value={data.privacy_page?.last_updated || ""}
                onChange={(e) => upd(["privacy_page", "last_updated"], e.target.value)} placeholder="February 2026" />
            </FieldRow>
            <div className="mt-4">
              <ParagraphsField testid="cms-privacy-body" value={data.privacy_page?.paragraphs}
                onCommit={(paras) => upd(["privacy_page", "paragraphs"], paras)} />
            </div>
          </Panel>
          <SaveBar keys={["privacy_page"]} name="privacy" />
        </TabsContent>

        {/* VERIFICATION */}
        <TabsContent value="verification">
          <Panel title="Verification process" subtitle="Full text shown on the /verification page. Separate paragraphs with a blank line.">
            <ParagraphsField testid="cms-verification-body" value={data.verification_page?.paragraphs}
              onCommit={(paras) => upd(["verification_page", "paragraphs"], paras)} />
          </Panel>
          <SaveBar keys={["verification_page"]} name="verification" />
        </TabsContent>

        {/* FOOTER */}
        <TabsContent value="footer">
          <Panel title="Footer">
            <Grid>
              <FieldRow label="Copyright text">
                <Input data-testid="cms-footer-copyright" className={inputCls} value={data.footer?.copyright || ""} onChange={(e) => upd(["footer", "copyright"], e.target.value)} />
              </FieldRow>
              <FieldRow label="Tagline" span="full">
                <Textarea data-testid="cms-footer-tagline" className={textareaCls} rows={2} value={data.footer?.tagline || ""} onChange={(e) => upd(["footer", "tagline"], e.target.value)} />
              </FieldRow>
            </Grid>
          </Panel>
          <SaveBar keys={["footer"]} name="footer" />
        </TabsContent>
      </Tabs>

      <div className="glass rounded-2xl p-5 flex items-start gap-3">
        <Info size={16} className="text-fuchsia-300 mt-0.5 shrink-0" />
        <div className="text-sm text-white/60 leading-relaxed">
          Changes save per tab and become live on the public site immediately (Home, About, Contact, Footer, Register form categories). No deployment needed.
        </div>
      </div>
    </div>
  );
}

// -------- helpers --------

function normalize(d) {
  return {
    website_name: d.website_name || "PRONET",
    logo_url: d.logo_url || "",
    social_links: d.social_links || [],
    contact_email: d.contact_email || "",
    contact_phone: d.contact_phone || "",
    admin_notification_email: d.admin_notification_email || "",
    whatsapp_number: d.whatsapp_number || "",
    telegram_link: d.telegram_link || "",
    hero: d.hero || {},
    home_features: d.home_features || [],
    home_banner_images: d.home_banner_images || [],
    home_banners: d.home_banners || [],
    categories: d.categories || [],
    locations: d.locations || { states: [], districts: [], cities: [] },
    about_page: d.about_page || {},
    contact_page: d.contact_page || {},
    privacy_page: d.privacy_page || {},
    verification_page: d.verification_page || {},
    footer: d.footer || {},
  };
}

const paragraphsToText = (arr) => (arr || []).join("\n\n");
const textToParagraphs = (text) => text.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);

// Committing on every keystroke (array -> joined text -> array) fights the
// cursor whenever a blank line is mid-typed, since a trailing blank
// paragraph gets filtered away before the next paragraph exists yet. Local
// text state + commit-on-blur avoids that entirely.
function ParagraphsField({ testid, value, onCommit }) {
  const [text, setText] = useState(() => paragraphsToText(value));
  useEffect(() => { setText(paragraphsToText(value)); }, [value]);
  return (
    <Textarea data-testid={testid} className={`${textareaCls} min-h-[280px] font-mono text-xs`}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onCommit(textToParagraphs(text))}
    />
  );
}

function setDeep(obj, path, val) {
  const p = [...path];
  let cur = obj;
  while (p.length > 1) {
    const k = p.shift();
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[p[0]] = val;
  return obj;
}

function reorder(setData, key, i, dir) {
  setData((s) => {
    const arr = [...(s[key] || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...s, [key]: arr };
  });
}
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function Panel({ title, subtitle, children }) {
  return (
    <section className="admin-surface mt-4 space-y-4 p-5 md:p-7 fade-up">
      <div>
        <div className="font-display text-2xl text-white">{title}</div>
        {subtitle && <div className="text-sm text-white/50 mt-1">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function Grid({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }

function FieldRow({ label, hint, children, span }) {
  return (
    <div className={span === "full" ? "md:col-span-2" : ""}>
      <label className="overline text-white/60 text-[10px]">{label}</label>
      <div className="mt-2">{children}</div>
      {hint && <div className="mt-1.5 text-xs text-white/40">{hint}</div>}
    </div>
  );
}

function IconBtn({ Icon, testid, onClick, disabled, tone }) {
  const color = tone === "red" ? "text-red-400 hover:bg-red-500/15" : "text-white/70 hover:text-fuchsia-300 hover:bg-white/5";
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`p-2 rounded-lg disabled:opacity-30 ${color}`}>
      <Icon size={14} />
    </button>
  );
}

function LocationList({ kind, items, onChange }) {
  const [val, setVal] = useState("");
  const singular = { states: "state", districts: "district", cities: "city" }[kind] || kind;
  return (
    <div className="glass rounded-2xl p-4">
      <div className="overline text-fuchsia-300/80 mb-3">{cap(kind)}</div>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 rounded-md bg-white/[0.03] px-2 py-1.5" data-testid={`cms-loc-${kind}-${i}`}>
            <span className="flex-1 text-sm text-white">{typeof it === "string" ? it : it.name}</span>
            <IconBtn testid={`cms-loc-${kind}-del-${i}`} tone="red" onClick={() => onChange(items.filter((_, idx) => idx !== i))} Icon={Trash2} />
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-white/40 py-2">No {kind} added yet.</div>}
      </div>
      <div className="mt-3 flex gap-2">
        <Input data-testid={`cms-loc-${kind}-input`} className="h-9 bg-white/5 border-white/10 text-white text-sm"
          value={val} onChange={(e) => setVal(e.target.value)} placeholder={`Add ${singular}…`}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (val.trim()) { onChange([...items, { name: val.trim() }]); setVal(""); } } }} />
        <button data-testid={`cms-loc-${kind}-add`}
          onClick={() => { if (val.trim()) { onChange([...items, { name: val.trim() }]); setVal(""); } }}
          className="btn-ghost inline-flex items-center gap-1 rounded-full px-3 h-9 text-xs font-semibold text-white">
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}
